import { Tooltip } from "@mui/material";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/helper/api";
import { useEffect, useState } from "react";
import { DotLoader } from "react-spinners";
import dayjs from "dayjs";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import { IDropDownOption } from "@/types/DropDown";
import { useErrorModal } from "@/components/forms/errorHandling/useErrorModal";
import GenericNotification from "@/components/notification/genericNotification";
import styles from "@/components/appointmentRequest/appointmentRequestTable.module.css";
import { getTranslatedStatusOptions } from "@/helper/statusOptions";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import EditSubjectDepartmentModal from "@/components/appointmentRequest/appointmentRequestEditSubjectDepartmentModal";
import NavigateToSubjectOverviewLink from "@/components/subjectOverview/navigateToSubjectOverviewLink";
import { useDispatch } from "react-redux";
import { setSubjectSearch } from "@/components/state/subjectSearch/subjectSearchSlice";
import SelectedSubjectDisplay from "@/components/general/selectedSubjectDisplay";
import AppointmentRequestSubjectsModal from "@/components/appointmentRequest/appointmentRequestSubjectsModal";
import { AppointmentRequestType } from "@/components/appointmentRequest/createNewAppointmentRequest";
import { replaceDate } from "@/helper/dateHandling";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { AppointmentRequestCompanySummary } from "@/components/appointmentRequest/appointmentRequestDepartmentOptions";
import type { IAppointmentRequest } from "@/types/Entities";
import { appointmentRequestEntityAdapter } from "@/components/appointmentRequest/appointmentRequestEntityAdapter";
import { appointmentRequestRowActions } from "@/components/appointmentRequest/appointmentRequestRowActions";

const APPOINTMENT_REQUEST_TYPES: AppointmentRequestType[] = [
  "standard_single",
  "standard_multiple",
  "single_company",
  "multiple_companies",
];

export function AppointmentRequestTable() {
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [addSubjectDepartmentModalOpen, setaddSubjectDepartmentModalOpen] =
    useState<boolean>(false);
  const [refreshTable, setRefreshTable] = useState<boolean>(false);
  const [
    companySubjectsForFilteringModal,
    setCompanySubjectsForFilteringModal,
  ] = useState<AppointmentRequestCompanySummary[]>([]);
  const [titleForFilteringModal, setTitleForFilteringModal] =
    useState<string>("");
  const [appointmentRequestId, setAppointmentRequestId] = useState<string>("");
  const [invitedSubjects, setInvitedSubjects] = useState<any>([]);
  const [excludedSubjects, setExcludedSubjects] = useState<any>([]);
  const [numberOfAllInvitedSubjects, setNumberOfAllInvitedSubjects] =
    useState<number>(0);
  const [numberOfAllExcludedSubjects, setNumberOfAllExcludedSubjects] =
    useState<number>(0);
  const [subjectModalType, setSubjectModalType] = useState<
    "invited" | "excluded"
  >("invited");
  const [
    selectedAppointmentRequestIdForExcluded,
    setSelectedAppointmentRequestIdForExcluded,
  ] = useState<string>("");
  const [excludedSubjectIdsBeingRemoved, setExcludedSubjectIdsBeingRemoved] =
    useState<string[]>([]);
  const [selectedExcludedSubjectIds, setSelectedExcludedSubjectIds] = useState<
    string[]
  >([]);
  const [isBatchRemovingExcludedSubjects, setIsBatchRemovingExcludedSubjects] =
    useState<boolean>(false);
  /*eslint-disable */
  const [options, setOptions] = useState<IDropDownOption[]>([]);
  /*eslint-enable */
  const [companySubjectId, setCompanySubjectId] = useState<string>("");
  const { error, handleError, errorModal } = useErrorModal();
  const [successForNotification, setSuccessForNotification] =
    useState<boolean>(false);
  const [notificationMessage, setNotificationMessage] = useState<any>("");
  const [notificationVariant, setNotificationVariant] = useState<
    "success" | "error" | "warning" | "info"
  >();

  const handleNotification = (
    variant: "success" | "error" | "warning" | "info",
    message: string,
    success: boolean
  ) => {
    setNotificationMessage(message);
    setSuccessForNotification(success);
    setNotificationVariant(variant);
  };

  const hostname = window.location.hostname;
  const subdomain = hostname.split(".")[0];

  const statusFilterOptions = getTranslatedStatusOptions("appointmentRequest");
  const { showConfirmationModalAsync } = useConfirmationModal();

  const navigate = useNavigate();
  const { t } = useTranslation();
  usePageHeading(t("appointmentrequest_single"));
  /*eslint-enable */
  const resetStateOnCloseNotification = () => {
    setSuccessForNotification(false);
  };
  const { subjectId: urlSubjectId } = useParams<{ subjectId?: string }>();
  const [urlSubjectName, setUrlSubjectName] = useState<string>("");
  const dispatch = useDispatch();

  useEffect(() => {
    if (!urlSubjectId) return;
    setCompanySubjectId(urlSubjectId);
    api.genericApiRequest({
      method: "get",
      entity: "subject",
      entityId: urlSubjectId,
      successHandler: (res: any) => {
        setUrlSubjectName(res.data?.name || "");
      },
      failHandler: () => {
        setUrlSubjectName("");
      },
    });
  }, [urlSubjectId]);

  const handleResetToSubjectSearch = () => {
    dispatch(setSubjectSearch({}));
    setUrlSubjectName("");
    setCompanySubjectId("");
    navigate("/appointmentrequest");
  };
  const sendReminderEmail = async (row: any, skipQuestion = false) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("send_reminder_for_appointmentRequest_confirm")
      );
      if (!isConfirmed) return;
    }
    setShowSpinner(true);
    const additionalRouteParts: any = {};

    additionalRouteParts["send_reminder_email"] = "/";

    api.genericApiRequest({
      entity: "appointmentRequest",
      method: "put",
      entityId: row.original.id,
      additionalRouteParts: additionalRouteParts,

      successHandler: () => {
        setShowSpinner(false);
        setNotificationMessage(t("successfully_sent_multi"));
        setSuccessForNotification(true);
        setNotificationVariant("success");
      },
      failHandler: () => {
        setShowSpinner(false);
        handleError(error);
      },
    });
  };

  const handleSetSnackBarforModal = () => {
    setNotificationMessage(t("save_successful"));
    setSuccessForNotification(true);
    setNotificationVariant("success");
    setRefreshTable((previousValue) => !previousValue);
  };

  const setAppointmentRequestStatus = async (
    row: any,
    futureStatus: string,
    skipQuestion = false
  ) => {
    const statusMapping: Record<string, string> = {
      [t("status_open")]: "open",
      [t("status_completed")]: "completed",
    };
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("change_status_to_confirm", {
          title: row.original.title,
          status: futureStatus,
        })
      );
      if (!isConfirmed) return;
    }
    const result = {
      status: statusMapping[futureStatus],
    };
    const additionalRouteParts: any = {};
    additionalRouteParts["set_status"] = "/";

    setShowSpinner(true);

    api.genericApiRequest({
      entity: "appointmentRequest",
      method: "post",
      successHandler: () => {
        setShowSpinner(false);
        setNotificationMessage(t("status_successfully_changed"));
        setSuccessForNotification(true);
        setNotificationVariant("success");
      },
      failHandler: (error: any) => {
        setShowSpinner(false);
        handleError(error);
      },
      entityId: row.original.id,
      additionalRouteParts: additionalRouteParts,
      parametersToRender: {
        depth: "0",
      },
      submitData: result,
    });
  };

  const reInviteSubjects = async (
    row: any,
    skipQuestion: false | undefined
  ) => {
    if (!skipQuestion) {
      const isConfirmed = await showConfirmationModalAsync(
        t("reinvite_subjects_question")
      );
      if (!isConfirmed) return;
    }
    setShowSpinner(true);
    const additionalRouteParts: any = {};
    additionalRouteParts["send_invites_to_new_subjects"] = "/";
    api.genericApiRequest({
      entity: "appointmentRequest",
      method: "put",
      successHandler: () => {
        setShowSpinner(false);
        setNotificationMessage(t("customer_all_new_successfully_added"));
        setSuccessForNotification(true);
        setNotificationVariant("success");
      },
      failHandler: (error: any) => {
        setShowSpinner(false);
        handleError(error);
      },
      entityId: row.original.id,
      additionalRouteParts: additionalRouteParts,
      parametersToRender: {
        depth: "0",
      },
    });
  };
  const openCreateHandler = () => {
    navigate("/appointmentbooking/");
  };

  const openEditHandler = (row: any) => {
    let editUrl: string;

    if (row.original.status !== "draft") {
      editUrl = `/editappointmentrequest/${row.original.id}/building`;
    } else {
      editUrl = `/editappointmentrequest/${row.original.id}/settings`;
    }

    if (row.allowOpenInNewTab) {
      window.open(editUrl, "_blank", "noopener,noreferrer");
    } else {
      navigate(editUrl);
    }
  };

  const openTimeslotEditHandler = (id: any) => {
    navigate(`/editappointmentrequest/${id}` + "/timeslots");
  };
  const handleShowInvitedPersons = (row: any) => {
    api.genericApiRequest({
      entity: "appointmentRequest",
      method: "get",
      successHandler: (res: any) => {
        const tempInvited = res.data.subject_info_set.sort((a: any, b: any) =>
          a.name.localeCompare(b.name)
        );
        setAppointmentRequestId(row.original.id);
        setInvitedSubjects(tempInvited);
        setNumberOfAllInvitedSubjects(tempInvited.length);
        setSubjectModalType("invited");
        setModalOpen(true);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      entityId: row.original.id,
    });
  };

  const handleShowExcludedPersons = (row: any) => {
    api.genericApiRequest({
      entity: "appointmentRequest",
      method: "get",
      successHandler: (res: any) => {
        const tempExcluded = (res.data.excluded_subject_set || []).sort(
          (a: any, b: any) => a.name.localeCompare(b.name)
        );
        setExcludedSubjects(tempExcluded);
        setNumberOfAllExcludedSubjects(tempExcluded.length);
        setSelectedAppointmentRequestIdForExcluded(row.original.id);
        setSelectedExcludedSubjectIds([]);
        setExcludedSubjectIdsBeingRemoved([]);
        setIsBatchRemovingExcludedSubjects(false);
        setSubjectModalType("excluded");
        setModalOpen(true);
      },
      failHandler: (error: any) => {
        handleError(error);
      },
      entityId: row.original.id,
    });
  };

  const handleToggleExcludedSubjectSelection = (subjectId: string) => {
    setSelectedExcludedSubjectIds((prev) =>
      prev.includes(subjectId)
        ? prev.filter((id) => id !== subjectId)
        : [...prev, subjectId]
    );
  };

  const handleRemoveSubjectsFromExcluded = (subjectIds: string[]) => {
    if (!selectedAppointmentRequestIdForExcluded || subjectIds.length === 0)
      return;
    const uniqueSubjectIds = Array.from(new Set(subjectIds));
    setExcludedSubjectIdsBeingRemoved((prev) =>
      Array.from(new Set([...prev, ...uniqueSubjectIds]))
    );
    setIsBatchRemovingExcludedSubjects(uniqueSubjectIds.length > 1);
    api.genericApiRequest({
      method: "put",
      entity: "appointmentRequest",
      entityId: selectedAppointmentRequestIdForExcluded,
      submitData: { person_subject_set: uniqueSubjectIds },
      additionalRouteParts: { remove_from_excluded: "/" },
      successHandler: () => {
        const removedCount = uniqueSubjectIds.length;
        setExcludedSubjects((prev: any[]) => {
          const filtered = prev.filter(
            (subject: any) => !uniqueSubjectIds.includes(String(subject.id))
          );
          setNumberOfAllExcludedSubjects(filtered.length);
          return filtered;
        });
        setSelectedExcludedSubjectIds((prev) =>
          prev.filter((id) => !uniqueSubjectIds.includes(id))
        );
        setNotificationMessage(
          t(
            removedCount === 1
              ? "appointmentrequest_remove_from_excluded_success"
              : "appointmentrequest_remove_from_excluded_success_multi",
            { number: removedCount }
          )
        );
        setSuccessForNotification(true);
        setNotificationVariant("success");
        setExcludedSubjectIdsBeingRemoved((prev) =>
          prev.filter((id) => !uniqueSubjectIds.includes(id))
        );
        setIsBatchRemovingExcludedSubjects(false);
      },
      failHandler: (error: any) => {
        setExcludedSubjectIdsBeingRemoved((prev) =>
          prev.filter((id) => !uniqueSubjectIds.includes(id))
        );
        setIsBatchRemovingExcludedSubjects(false);
        handleError(error);
      },
    });
  };

  const handleRemoveSubjectFromExcluded = (subjectId: string) => {
    if (!selectedAppointmentRequestIdForExcluded) return;
    handleRemoveSubjectsFromExcluded([subjectId]);
  };

  const handleSubjectSelect = (selectedOption: any) => {
    if (
      selectedOption &&
      typeof selectedOption === "object" &&
      selectedOption.value &&
      selectedOption.companyId
    ) {
      setCompanySubjectId(selectedOption.value);
    }
  };

  const resetAsyncInput = () => {
    setCompanySubjectId("");
  };

  const handleSetSubjectDepartmentModalOpen = (row: any) => {
    setAppointmentRequestId(row.original?.id);
    setCompanySubjectsForFilteringModal(
      row.original?.company_subject_set ?? []
    );
    setTitleForFilteringModal(row.original?.title);
    setaddSubjectDepartmentModalOpen(true);
  };

  const handleSetSubjectDepartmentModal = (value: boolean) => {
    setaddSubjectDepartmentModalOpen(value);
  };

  const appointmentRequestTypeFilterOptions: IDropDownOption[] =
    APPOINTMENT_REQUEST_TYPES.map((type) => ({
      value: type,
      label: type,
    }));
  const customRowActions = (row: any) => {
    const actualRow = row.original;

    const shouldRenderDepartmentModal =
      actualRow.type === "standard_multiple" ||
      actualRow.type === "standard_single";

    const status = actualRow.status;
    return (
      <>
        {status === "open" && (
          <>
            <Tooltip
              arrow
              placement="top"
              enterDelay={1000}
              enterNextDelay={250}
              title={t("change_status_to", { status: t("status_completed") })}
            >
              <GenericButton
                variant="icon"
                color="iconBase"
                onClick={() => {
                  setAppointmentRequestStatus(row, t("status_completed"));
                }}
              >
                <LockIcon />
              </GenericButton>
            </Tooltip>
            <Tooltip
              arrow
              placement="top"
              enterDelay={1000}
              enterNextDelay={250}
              title={t("send_reminder_for_appointmentRequest")}
            >
              <GenericButton
                variant="icon"
                color="iconBase"
                onClick={() => {
                  sendReminderEmail(row);
                }}
              >
                <NotificationsNoneIcon />
              </GenericButton>
            </Tooltip>
            {shouldRenderDepartmentModal && (
              <Tooltip
                arrow
                placement="top"
                enterDelay={1000}
                enterNextDelay={250}
                title={t("edit_subject_and_or_department")}
              >
                <GenericButton
                  variant="icon"
                  color="iconBase"
                  onClick={() => handleSetSubjectDepartmentModalOpen(row)}
                >
                  <SyncAltIcon />
                </GenericButton>
              </Tooltip>
            )}
          </>
        )}
        {status === "completed" && (
          <Tooltip
            arrow
            placement="top"
            enterDelay={1000}
            enterNextDelay={250}
            title={t("change_status_to", { status: t("status_open") })}
          >
            <GenericButton
              variant="icon"
              color="iconBase"
              onClick={() => {
                setAppointmentRequestStatus(row, t("status_open"));
              }}
            >
              <LockOpenIcon />
            </GenericButton>
          </Tooltip>
        )}
      </>
    );
  };

  return (
    <>
      {showSpinner ? (
        <DotLoader
          color="#8c1ec8"
          size={65}
          cssOverride={{ position: "absolute", top: "45vh", left: "50vw" }}
        />
      ) : (
        <>
          {urlSubjectId ? (
            <SelectedSubjectDisplay
              wrapperClassName={styles.dropDownSubjectSearch}
              subjectName={urlSubjectName}
              onReset={handleResetToSubjectSearch}
            />
          ) : (
            <div className={styles.dropDownSubjectSearch}>
              <FormFieldLabel
                label={t("company")}
                htmlFor="appointment-request-company"
              />
              <DropDownSubjectSearch
                name="appointment-request-company"
                options={options}
                onChange={handleSubjectSelect}
                subdomain={subdomain}
                isClearable={true}
                resetInput={resetAsyncInput}
                queryParameter="companies"
                useGlobalState={true}
                autofocus={true}
              />
            </div>
          )}
          <>
            <GenericCrudTable
              dataTestId="appointment-request-table"
              searchId={companySubjectId}
              entity={"appointmentRequest"}
              entityAdapter={appointmentRequestEntityAdapter}
              allowColumnFiltering={true}
              showColumnFilterbyDefault={true}
              customRowActions={customRowActions}
              refreshTable={refreshTable}
              disableEditButtonForRow={(row: any) =>
                row.original?.status === "completed"
              }
              // customRowActionColSize={250}
              customColumnVisibility={{
                parallel_slots: false,
                created_on: false,
              }}
              columns={[
                { header: t("id"), accessorKey: "id", id: "id" },
                {
                  header: t("type"),
                  accessorKey: "type",
                  id: "type",
                  filterVariant: "select",
                  filterSelectOptions: appointmentRequestTypeFilterOptions,
                },
                {
                  header: t("status"),
                  accessorKey: "status",
                  filterVariant: "select",
                  filterSelectOptions: statusFilterOptions,
                  id: "status",
                  accessorFn: (row: any) => {
                    const status = row.status;
                    const string = "status_" + status;

                    return `${t(string)} (${status})`;
                  },
                },
                {
                  header: t("appointment_request_email_mode_column"),
                  accessorKey: "email_mode",
                  id: "email_mode",
                  filterVariant: "select",
                  setNoFilterParameter: true,
                  filterSelectOptions: [
                    "all_emails",
                    "initial_only",
                    "manual_only",
                  ].map((value) => ({
                    value,
                    label: t(`appointment_request_email_mode_short_${value}`),
                  })),
                  accessorFn: (row: IAppointmentRequest) =>
                    row.email_mode
                      ? t(
                          `appointment_request_email_mode_short_${row.email_mode}`
                        )
                      : "-",
                  size: 260,
                },
                {
                  header: `${t("company")}/${t("companies")}`,
                  accessorKey: "name",
                  id: "company",
                  customColumnFilterId: "company_name",
                  customSortingKey: "company_name",
                  accessorFn: (row: any) => {
                    if (
                      row.type === "single_company" ||
                      row.type === "standard_single"
                    ) {
                      const companySubjectId = row.company_subject_set[0]?.id;
                      const companyName = row.company_subject_set[0]?.name;

                      return (
                        <NavigateToSubjectOverviewLink
                          subjectId={companySubjectId}
                          label={companyName}
                        />
                      );
                    }
                    if (row?.company_subject_set.length !== 0) {
                      const maxToShow = 5;
                      const displayedCompanies = row?.company_subject_set.slice(
                        0,
                        maxToShow
                      );
                      const wholeCount = row?.company_subject_set.length;

                      return (
                        <>
                          {wholeCount > maxToShow ? (
                            <span>
                              {t("appointmentrequest_invited_companies", {
                                count: wholeCount,
                              })}
                            </span>
                          ) : (
                            displayedCompanies.map(({ id, name }: any) => (
                              <NavigateToSubjectOverviewLink
                                key={id}
                                subjectId={id}
                                label={name}
                                className={styles.multiCompanyLink}
                                shouldNavigate={false}
                              />
                            ))
                          )}
                        </>
                      );
                    }
                    return null;
                  },
                  size: 400,
                },
                {
                  header: t("department"),
                  accessorKey: "department_set",
                  id: "department_set",
                  customColumnFilterId: "department_set__name",
                  customSortingKey: "department_name",
                  accessorFn: (row: any) => {
                    const departments = [];
                    if (row.department_set) {
                      for (const key in row.department_set) {
                        if (row.department_set[key].name) {
                          departments.push(row.department_set[key].name);
                        }
                      }
                    }
                    return departments.join(", ");
                  },
                },
                {
                  header: t("category"),
                  accessorKey: "category",
                  id: "category",
                  customColumnFilterId: "category__name",
                  customSortingKey: "category_name",
                  accessorFn: (row: any) => {
                    if (row.category) {
                      const name = row.category.name;
                      return name;
                    }
                  },
                },
                {
                  header: t("name"),
                  accessorKey: "title",
                  id: "title",
                },
                {
                  header: t("date"),
                  accessorKey: "day_set",
                  id: "day_set",
                  customColumnFilterId: "day_set",
                  customSortingKey: "day_set__start",
                  filterVariant: "date",
                  filterDateRangeFormat: "date",
                  dateFilterApiParams: {
                    exact: "day_set_date",
                    gt: "day_set_gt",
                    lt: "day_set_lt",
                    shiftExclusiveDateBounds: true,
                  },
                  sortingFn: "date",

                  accessorFn: (row: any) => {
                    const daysInRange = row.day_set.length;
                    let date;
                    if (row.day_set.length !== 0) {
                      const set = row.day_set;
                      const start = dayjs(set[0].start).format("DD.MM.YYYY");
                      const end = dayjs(set[daysInRange - 1].end).format(
                        "DD.MM.YYYY"
                      );

                      if (start === end) {
                        date = start;
                      } else {
                        date = start + " - " + end;
                      }
                      // }
                      return date;
                    }
                  },
                },
                {
                  header: t("parallel_slots"),
                  accessorKey: "parallel_slots",
                  id: "parallel_slots",
                },
                {
                  header: t("duration"),
                  accessorKey: "slot_duration",
                  id: "slot_duration",
                  size: 120,
                },
                {
                  header: t("on_site"),
                  id: "on_site",
                  filterVariant: "select",

                  filterSelectOptions: [
                    { value: "true", label: t("yes") },
                    { value: "false", label: t("no") },
                  ],
                  setNoFilterParameter: true,
                  accessorFn: (row: any) => {
                    if (row.on_site) {
                      return t("yes");
                    } else return t("no");
                  },
                  enableForCreate: true,

                  selectOptions: [
                    { value: false, label: t("no") },
                    { value: true, label: t("yes") },
                  ],
                },
                {
                  header: t("executed_by"),
                  id: "executed_by",
                  customColumnFilterId: "executed_by_name",
                  customSortingKey: "executed_by_name",

                  accessorKey: "executed_by",
                  accessorFn: (row: any) => {
                    if (row.executed_by && row.executed_by !== null) {
                      return row.executed_by.name;
                    } else return "-";
                  },
                },
                {
                  header: t("assisted_by"),
                  accessorKey: "assisted_by",
                  id: "assisted_by",
                  customColumnFilterId: "assisted_by_name",
                  customSortingKey: "assisted_by_name",
                  accessorFn: (row: any) => {
                    if (row.assisted_by && row.assisted_by !== null) {
                      return row.assisted_by.name;
                    } else return "-";
                  },
                },
                {
                  header: t("customer_information"),
                  accessorKey: "customer_information",
                  id: "customer_information",
                  customColumnFilterId:
                    "building_information__customer_information",
                  customSortingKey: "customer_information",
                  accessorFn: (row: any) => {
                    if (
                      row.building_information.customer_information &&
                      row.building_information.customer_information !== null
                    ) {
                      return row.building_information.customer_information;
                    } else return "-";
                  },
                },
                {
                  header: t("created_on"),
                  accessorKey: "created_on",
                  id: "created_on",
                  filterVariant: "date",

                  sortingFn: "datetime",
                  Cell: ({ cell }: any) => {
                    return (
                      <span>{replaceDate(cell.getValue(), true, true)}</span>
                    );
                  },
                },
                {
                  header: t("created_by"),
                  accessorKey: "created_by",
                  customColumnFilterId: "created_by_name",
                  id: "created_by",
                  Cell: ({ cell }: any) => {
                    return <span>{cell.getValue()?.name}</span>;
                  },
                },
                {
                  header: t("modified_on"),
                  accessorKey: "modified_on",
                  id: "modified_on",
                  filterVariant: "date",
                  sortingFn: "datetime",
                  Cell: ({ cell }: any) => {
                    return (
                      <span>{replaceDate(cell.getValue(), true, true)}</span>
                    );
                  },
                },
                {
                  header: t("modified_by"),
                  accessorKey: "modified_by",
                  customColumnFilterId: "modified_by_name",
                  id: "modified_by",
                  Cell: ({ cell }: any) => {
                    return <span>{cell.getValue()?.name}</span>;
                  },
                },
              ]}
              rowActions={appointmentRequestRowActions({
                t,
                openDraft: (row) =>
                  setAppointmentRequestStatus(row, t("status_open")),
                reinviteSubjects: (row) => reInviteSubjects(row, undefined),
                showInvitedPersons: handleShowInvitedPersons,
                showExcludedPersons: handleShowExcludedPersons,
                editTimeslots: openTimeslotEditHandler,
              })}
              allowOpenEditInNewTab
              customEditHandler={openEditHandler}
              customCreateHandler={openCreateHandler}
              additionalUrlParameter={{ include_draft: "true" }}
            />
          </>
          {errorModal}

          <GenericNotification
            message={successForNotification ? notificationMessage : null}
            variant={notificationVariant}
            handleCloseSnackbar={resetStateOnCloseNotification}
          />
        </>
      )}
      <AppointmentRequestSubjectsModal
        open={modalOpen}
        subjectModalType={subjectModalType}
        invitedSubjects={invitedSubjects}
        numberOfAllInvitedSubjects={numberOfAllInvitedSubjects}
        excludedSubjects={excludedSubjects}
        numberOfAllExcludedSubjects={numberOfAllExcludedSubjects}
        selectedExcludedSubjectIds={selectedExcludedSubjectIds}
        excludedSubjectIdsBeingRemoved={excludedSubjectIdsBeingRemoved}
        isBatchRemovingExcludedSubjects={isBatchRemovingExcludedSubjects}
        onToggleExcludedSubjectSelection={handleToggleExcludedSubjectSelection}
        onRemoveExcludedSubject={handleRemoveSubjectFromExcluded}
        onRemoveSelectedExcludedSubjects={handleRemoveSubjectsFromExcluded}
        appointmentRequestId={appointmentRequestId}
        onClearExcludedSelection={() => {
          setSelectedExcludedSubjectIds([]);
        }}
        handleNotification={handleNotification}
        handleError={handleError}
        onClose={() => {
          setModalOpen(false);
          setSelectedExcludedSubjectIds([]);
          setExcludedSubjectIdsBeingRemoved([]);
          setIsBatchRemovingExcludedSubjects(false);
        }}
      />
      <EditSubjectDepartmentModal
        modalOpen={addSubjectDepartmentModalOpen}
        subdomain={subdomain}
        companySubjects={companySubjectsForFilteringModal}
        title={titleForFilteringModal}
        handleSetError={handleError}
        handleSetModalState={handleSetSubjectDepartmentModal}
        appointmentRequestId={appointmentRequestId}
        handleShowNotification={handleSetSnackBarforModal}
      />
    </>
  );
}

// TODO: @BEN bring remaining rowactions from crudtable to customrowactions in here
